import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ConfidenceChip } from '@/components/ai-accountant/confidence-chip';
import { Banner, FilePane } from '@/components/ai-accountant/review-parts';
import { StatementReview } from '@/components/ai-accountant/statement-review';
import { AiStatusChip } from '@/components/ai-accountant/status-chip';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { AmountInput } from '@/components/amount-input';
import { Combobox, type ComboItem } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAccounts, useTaxCodes, taxCodesFor, useAccountNames } from '@/lib/accounts';
import { ltr, nameFor } from '@/lib/arabic';
import {
  initialForm,
  recordAsLabel,
  minorToDecimal,
  reviewBody,
  statementOf,
  lowFieldLabel,
  unconfirmedLow,
  useAiDocument,
  type AiDocumentDetail,
  type Field as AiField,
  type RecordAs,
  type ReviewForm,
  type Voucher,
} from '@/lib/ai-accountant';
import { api } from '@/lib/api';
import { useContacts } from '@/lib/contacts';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDateTime } from '@/lib/format';
import { GST_STATES } from '@/lib/gst';
import { useLanguage, useT } from '@/lib/i18n';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { isIndia, useHomeCurrency } from '@/lib/org-locale';
import { documentPath, errorText, isClientRole, useOrgRole } from '@/lib/portal';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/ai-accountant/$id')({ component: Review });

/**
 * Screen AI2 (document 06): review one AI Accountant proposal, QBO Receipts "For review" style.
 * Left, the file and the text read from it; right, what to record with a confidence chip on each
 * suggested field, the line grid, totals and the voucher it will post. Approve and create records
 * it; nothing is recorded before.
 */
function Review() {
  const t = useT();
  const { id } = Route.useParams();
  const role = useOrgRole();
  const doc = useAiDocument(id);
  if (role && isClientRole(role)) return <EmptyState title={t('reportPages.ai.list.staffOnly')} />;
  if (doc.isPending) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (doc.isError)
    return <EmptyState title={t('reportPages.ai.review.loadError')} hint={doc.error.message} />;
  const d = doc.data;
  // a bank statement is reviewed transaction by transaction and sent to Banking (AI3)
  const statement = statementOf(d);
  if (statement)
    return <StatementReview key={`${d.id}:${d.version}:${d.status}`} d={d} p={statement} />;
  return <ReviewScreen key={`${d.id}:${d.version}:${d.status}`} d={d} />;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function ReviewScreen({ d }: { d: AiDocumentDetail }) {
  const t = useT();
  const lang = useLanguage();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const home = useHomeCurrency();
  const p = d.proposal;
  const editable = d.status === 'for_review';
  const [form, setForm] = useState<ReviewForm>(() => initialForm(d, todayIso()));
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [confirmingLow, setConfirmingLow] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = (key: string) => setConfirmed((s) => new Set(s).add(key));
  const set = <K extends keyof ReviewForm>(k: K, v: ReviewForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i: number, patch: Partial<ReviewForm['lines'][number]>) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, n) => (n === i ? { ...l, ...patch } : l)) }));

  const accounts = useAccounts();
  const taxCodes = useTaxCodes();
  const vendors = useContacts({ kind: 'vendor' });
  const customers = useContacts({ kind: 'customer' });
  const india = isIndia();
  // a sales invoice issued elsewhere: customer, income accounts and sales tax codes
  const sale = form.recordAs === 'invoice';

  const lineAccounts: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? [])
        .filter(
          (a) =>
            a.isActive &&
            !a.isControl &&
            (sale
              ? ['income', 'other_income'].includes(a.accountType)
              : !['bank', 'credit_card'].includes(a.accountType) &&
                a.detailType !== 'undeposited_funds'),
        )
        .map((a) => ({
          id: a.id,
          label: `${a.code} ${nameFor(lang, a.fullName, a.fullNameAr)}`,
          keywords: `${a.accountTypeName} ${a.detailTypeName} ${a.fullName} ${a.fullNameAr ?? ''}`,
          group: nameFor(lang, a.accountTypeName, a.accountTypeNameAr),
          hint: nameFor(lang, a.detailTypeName, a.detailTypeNameAr),
        })),
    [accounts.data, sale, lang],
  );
  const paymentAccounts = (accounts.data ?? []).filter(
    (a) => a.isActive && ['bank', 'savings', 'cash_on_hand', 'credit_card'].includes(a.detailType),
  );
  const creditAccounts: ComboItem[] = useMemo(
    () =>
      (accounts.data ?? [])
        .filter((a) => a.isActive && !a.isControl)
        .map((a) => ({
          id: a.id,
          label: `${a.code} ${nameFor(lang, a.fullName, a.fullNameAr)}`,
          keywords: `${a.fullName} ${a.fullNameAr ?? ''}`,
          group: nameFor(lang, a.accountTypeName, a.accountTypeNameAr),
        })),
    [accounts.data, lang],
  );
  // India: CGST + SGST codes only; the API switches to IGST from the state
  const lineCodes = taxCodesFor(taxCodes.data, sale ? 'sales' : 'purchases').filter(
    (t) => !(india && t.gstSplit === 'igst'),
  );
  const contacts = sale ? customers.data : vendors.data;
  const partyItems: ComboItem[] = useMemo(
    () =>
      (contacts ?? []).map((v) => ({
        id: v.id,
        label: nameFor(lang, v.displayName, v.displayNameAr),
        hint: v.gstin ?? undefined,
        keywords: `${v.gstin ?? ''} ${v.displayName} ${v.displayNameAr ?? ''}`,
      })),
    [contacts, lang],
  );

  const ready =
    editable &&
    form.lines.length > 0 &&
    form.lines.every((l) => l.amount && Number.isFinite(Number(l.amount))) &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.issueDate);
  const debounced = useDebounced(form, 400);
  const preview = useQuery({
    queryKey: ['ai-accountant', orgId, 'preview', d.id, debounced],
    queryFn: () =>
      api<{ voucher: Voucher; subtotal: string; tax: string; total: string }>(
        `/orgs/${orgId}/ai-accountant/${d.id}/preview`,
        { method: 'POST', body: JSON.stringify(reviewBody(debounced)) },
      ),
    enabled: Boolean(orgId) && ready,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const after = () => qc.invalidateQueries({ queryKey: ['ai-accountant', orgId] });
  const save = useMutation({
    mutationFn: () =>
      api<AiDocumentDetail>(`/orgs/${orgId}/ai-accountant/${d.id}/draft`, {
        method: 'PUT',
        body: JSON.stringify({ ...reviewBody(form), version: d.version }),
      }),
    onSuccess: () => toast(t('reportPages.ai.review.saved')),
    onError: (e) => setError(errorText(e)),
    onSettled: after,
  });
  const approve = useMutation({
    mutationFn: () =>
      api<{ created: { type: RecordAs; id: string; number: string | null } }>(
        `/orgs/${orgId}/ai-accountant/${d.id}/approve`,
        { method: 'POST', body: JSON.stringify({ ...reviewBody(form), version: d.version }) },
      ),
    onSuccess: (r) => {
      const c = r.created;
      toast(
        t('reportPages.ai.review.created', {
          kind: recordAsLabel(c.type),
          number: c.number ? ltr(c.number) : '',
          file: d.fileName,
        }),
        {
          link: {
            to:
              c.type === 'journal_entry'
                ? `/accounting/journal-entries/${c.id}`
                : documentPath(c.type, c.id),
            label: t('reportPages.ai.list.view'),
          },
        },
      );
      void navigate({ to: '/ai-accountant', search: { status: 'for_review' } });
    },
    onError: (e) => setError(errorText(e)),
    onSettled: async () => {
      await after();
      await qc.invalidateQueries({ queryKey: ['portal', orgId] });
    },
  });
  const rerun = useMutation({
    mutationFn: () =>
      api(`/orgs/${orgId}/ai-accountant/${d.id}/rerun`, {
        method: 'POST',
        body: JSON.stringify({ version: d.version }),
      }),
    onSuccess: () => toast(t('reportPages.ai.list.sentAgain')),
    onError: (e) => setError(errorText(e)),
    onSettled: after,
  });
  const reject = useMutation({
    mutationFn: (reason: string) =>
      api(`/orgs/${orgId}/ai-accountant/${d.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason, version: d.version }),
      }),
    onSuccess: () => {
      setRejecting(false);
      toast(t('reportPages.ai.review.rejectedToast', { name: d.fileName }));
    },
    onSettled: after,
  });
  const reopen = useMutation({
    mutationFn: () =>
      api(`/orgs/${orgId}/ai-accountant/${d.id}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ version: d.version }),
      }),
    onSettled: after,
  });

  const low = unconfirmedLow(p, form, confirmed);
  const voucher = preview.data?.voucher ?? null;
  const missingInvoiceNo = sale && !form.billNo?.trim();
  const canApprove =
    editable && ready && !missingInvoiceNo && Boolean(voucher?.balanced) && !preview.isFetching;
  const edited = (key: string, now: unknown, field: AiField<unknown> | undefined | null) =>
    Boolean(field) &&
    field!.value !== now &&
    !(field!.value === null && (now === null || now === ''));
  const chip = (
    key: string,
    field: AiField<unknown> | undefined | null,
    now: unknown,
    onPick?: (v: unknown) => void,
  ) =>
    p && field ? (
      <ConfidenceChip
        testId={`chip-${key}`}
        field={field}
        edited={edited(key, now, field)}
        confirmed={confirmed.has(key)}
        onConfirm={() => confirm(key)}
        {...(onPick && editable ? { onPick } : {})}
      />
    ) : null;
  const printedTotal = p?.totalMinor.value ? minorToDecimal(p.totalMinor.value) : null;
  const ccy = form.currency || home;

  return (
    <div className="pb-20">
      <Link
        to="/ai-accountant"
        search={{ status: 'for_review' }}
        className="mb-3 inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="rtl:-scale-x-100 size-4" aria-hidden />{' '}
        {t('reportPages.ai.review.back')}
      </Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.7rem] font-medium tracking-[-0.01em]">
            {d.fileName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {d.uploadedBy
              ? t('reportPages.ai.review.uploadedBy', {
                  date: formatDateTime(d.uploadedAt),
                  name: d.uploadedBy,
                })
              : t('reportPages.ai.review.uploaded', { date: formatDateTime(d.uploadedAt) })}
            {d.inbox
              ? ` · ${
                  d.inbox.uploadedBy
                    ? t('reportPages.ai.review.fromInboxBy', { name: d.inbox.uploadedBy })
                    : t('reportPages.ai.review.fromInbox')
                }`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <AiStatusChip status={d.status} />
          {p ? (
            <span
              data-testid="engine"
              className="inline-flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-xs"
            >
              <Sparkles className="size-3 text-primary" aria-hidden />
              {p.engine === 'jev'
                ? d.model
                  ? t('reportPages.ai.review.suggestedJevModel', { model: d.model })
                  : t('reportPages.ai.review.suggestedJev')
                : t('reportPages.ai.review.suggestedRules')}
            </span>
          ) : null}
        </div>
      </div>

      {p?.notice ? <Banner tone="warning">{p.notice}</Banner> : null}
      {d.status === 'processing' ? (
        <Banner tone="info">
          <Loader2 className="size-4 animate-spin" aria-hidden />{' '}
          {t('reportPages.ai.review.processing')}
        </Banner>
      ) : null}
      {d.status === 'failed' ? (
        <Banner tone="error">
          {d.error
            ? t('reportPages.ai.review.failed', { error: d.error })
            : t('reportPages.ai.review.failedNoError')}
        </Banner>
      ) : null}
      {d.status === 'rejected' ? (
        <Banner tone="error">
          {d.reviewedBy
            ? t('reportPages.ai.review.rejectedBy', {
                name: d.reviewedBy,
                reason: d.rejectReason ?? '',
              })
            : t('reportPages.ai.review.rejected', { reason: d.rejectReason ?? '' })}
          <Button size="sm" variant="outline" className="ms-auto" onClick={() => reopen.mutate()}>
            {t('reportPages.ai.review.backToReview')}
          </Button>
        </Banner>
      ) : null}
      {d.status === 'recorded' ? (
        <Banner tone="success">
          <CheckCircle2 className="size-4" aria-hidden />
          {d.reviewedBy && d.reviewedAt
            ? t('reportPages.ai.review.recordedByOnAs', {
                name: d.reviewedBy,
                date: formatDateTime(d.reviewedAt),
              })
            : d.reviewedBy
              ? t('reportPages.ai.review.recordedByAs', { name: d.reviewedBy })
              : d.reviewedAt
                ? t('reportPages.ai.review.recordedOnAs', { date: formatDateTime(d.reviewedAt) })
                : t('reportPages.ai.review.recordedAs')}{' '}
          {d.document ? (
            <Link
              className="font-medium text-primary hover:underline"
              to={documentPath(d.document.kind, d.document.id)}
            >
              {t(
                d.document.kind === 'expense'
                  ? 'reportPages.ai.list.docExpense'
                  : d.document.kind === 'invoice'
                    ? 'reportPages.ai.list.docInvoice'
                    : 'reportPages.ai.list.docBill',
                { number: d.document.number ?? t('reportPages.ai.review.draftNumber') },
              )}
            </Link>
          ) : d.journalEntry ? (
            <Link
              className="font-medium text-primary hover:underline"
              to="/accounting/journal-entries/$entryId"
              params={{ entryId: d.journalEntry.id }}
            >
              {t('reportPages.ai.review.journalEntryNo', { number: d.journalEntry.entryNo })}
            </Link>
          ) : null}
          .
        </Banner>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <FilePane d={d} />

        <section className="space-y-4" aria-label={t('reportPages.ai.review.whatToRecord')}>
          <ErrorBanner>{error}</ErrorBanner>
          {p?.checks.filter((c) => !c.ok).length ? (
            <ul
              className="space-y-1 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm"
              data-testid="checks"
            >
              {p.checks
                .filter((c) => !c.ok)
                .map((c, i) => (
                  <li key={i} className="flex gap-2">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                    {c.message}
                  </li>
                ))}
            </ul>
          ) : null}

          <fieldset disabled={!editable} className="space-y-4 rounded-lg border bg-card p-4">
            <div>
              <div className="mb-1 flex items-center gap-2">
                <span className="text-sm font-medium">{t('reportPages.ai.lowField.recordAs')}</span>
                {chip('recordAs', p?.recordAs, form.recordAs, (v) =>
                  set('recordAs', v as RecordAs),
                )}
              </div>
              <div
                className="inline-flex rounded-md border"
                role="radiogroup"
                aria-label={t('reportPages.ai.lowField.recordAs')}
              >
                {(['bill', 'expense', 'journal_entry', 'invoice'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={form.recordAs === k}
                    disabled={!editable || (k === 'journal_entry' && Boolean(d.inbox))}
                    title={
                      k === 'journal_entry' && d.inbox
                        ? t('reportPages.ai.review.inboxJournalTitle')
                        : undefined
                    }
                    className={cn(
                      'px-3 py-1.5 text-sm first:rounded-s-md last:rounded-e-md disabled:opacity-50',
                      form.recordAs === k
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card hover:bg-muted',
                    )}
                    onClick={() => set('recordAs', k)}
                  >
                    {recordAsLabel(k)}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {sale ? (
                <PartyPicker
                  party="customer"
                  chip={chip('customer', p?.customer, form.customerId, (v) =>
                    setForm((f) => ({ ...f, customerId: v as string, newCustomer: null })),
                  )}
                  items={partyItems}
                  value={form.customerId}
                  fresh={form.newCustomer}
                  india={india}
                  read={{
                    name: p?.newCustomer?.name ?? p?.customerName?.value ?? null,
                    gstin: p?.newCustomer?.gstin ?? p?.customerGstin?.value ?? null,
                  }}
                  onPick={(v) => setForm((f) => ({ ...f, customerId: v, newCustomer: null }))}
                  onFresh={(n) =>
                    setForm((f) => ({ ...f, customerId: n ? null : f.customerId, newCustomer: n }))
                  }
                />
              ) : (
                <PartyPicker
                  party="vendor"
                  chip={chip('vendor', p?.vendor, form.vendorId, (v) =>
                    setForm((f) => ({ ...f, vendorId: v as string, newVendor: null })),
                  )}
                  items={partyItems}
                  value={form.vendorId}
                  fresh={form.newVendor}
                  india={india}
                  read={{
                    name: p?.newVendor?.name ?? p?.vendorName.value ?? null,
                    gstin: p?.newVendor?.gstin ?? p?.supplierGstin.value ?? null,
                  }}
                  onPick={(v) => setForm((f) => ({ ...f, vendorId: v, newVendor: null }))}
                  onFresh={(n) =>
                    setForm((f) => ({ ...f, vendorId: n ? null : f.vendorId, newVendor: n }))
                  }
                />
              )}
              <Labelled
                label={t(sale ? 'reportPages.ai.review.invoiceNo' : 'reportPages.ai.review.billNo')}
                chip={chip('billNo', p?.billNo, form.billNo)}
              >
                <Input
                  aria-label={t(
                    sale ? 'reportPages.ai.review.invoiceNo' : 'reportPages.ai.review.billNo',
                  )}
                  required={sale}
                  aria-invalid={missingInvoiceNo || undefined}
                  placeholder={sale ? t('reportPages.ai.review.asPrinted') : undefined}
                  value={form.billNo ?? ''}
                  onChange={(e) => set('billNo', e.target.value || null)}
                />
              </Labelled>
              <Labelled
                label={
                  form.recordAs === 'journal_entry'
                    ? t('reportPages.ai.review.journalDate')
                    : sale
                      ? t('reportPages.ai.lowField.invoiceDate')
                      : t('reportPages.ai.lowField.billDate')
                }
                chip={chip('issueDate', p?.issueDate, form.issueDate)}
              >
                <DateInput
                  aria-label={t(
                    sale
                      ? 'reportPages.ai.lowField.invoiceDate'
                      : 'reportPages.ai.lowField.billDate',
                  )}
                  value={form.issueDate}
                  onChange={(v) => set('issueDate', v)}
                />
              </Labelled>
              {form.recordAs === 'bill' || sale ? (
                <Labelled
                  label={t('reportPages.ai.review.dueDate')}
                  chip={chip('dueDate', p?.dueDate, form.dueDate)}
                >
                  <DateInput
                    aria-label={t('reportPages.ai.review.dueDate')}
                    value={form.dueDate ?? ''}
                    onChange={(v) => set('dueDate', v || null)}
                  />
                </Labelled>
              ) : null}
              {india ? (
                <Labelled
                  label={t('reportPages.ai.review.placeOfSupply')}
                  chip={chip('placeOfSupply', p?.placeOfSupply, form.placeOfSupply)}
                >
                  <NativeSelect
                    aria-label={t('reportPages.ai.review.placeOfSupply')}
                    value={form.placeOfSupply ?? ''}
                    onChange={(e) => set('placeOfSupply', e.target.value || null)}
                  >
                    <option value="">—</option>
                    {GST_STATES.map(([code, name]) => (
                      <option key={code} value={code}>
                        {code}-{name}
                      </option>
                    ))}
                  </NativeSelect>
                </Labelled>
              ) : null}
              <Labelled
                label={t('reportPages.ai.review.currency')}
                chip={chip('currency', p?.currency, form.currency)}
              >
                <NativeSelect
                  aria-label={t('reportPages.ai.review.currency')}
                  value={form.currency}
                  onChange={(e) => set('currency', e.target.value)}
                >
                  {[...new Set([home, form.currency, p?.currency.value ?? home])].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </NativeSelect>
              </Labelled>
              {form.recordAs === 'expense' ? (
                <Labelled
                  label={t('reportPages.ai.review.paidFrom')}
                  chip={chip('paymentAccount', p?.paymentAccount, form.paymentAccountId)}
                >
                  <NativeSelect
                    aria-label={t('reportPages.ai.review.paidFrom')}
                    value={form.paymentAccountId ?? ''}
                    onChange={(e) => set('paymentAccountId', e.target.value || null)}
                  >
                    <option value="">{t('reportPages.ai.review.chooseAccount')}</option>
                    {paymentAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {nameFor(lang, a.fullName, a.fullNameAr)}
                      </option>
                    ))}
                  </NativeSelect>
                </Labelled>
              ) : null}
              {form.recordAs === 'journal_entry' ? (
                <Labelled label={t('reportPages.ai.review.creditAccount')}>
                  <Combobox
                    ariaLabel={t('reportPages.ai.review.creditAccount')}
                    items={creditAccounts}
                    value={form.paymentAccountId}
                    onChange={(v) => set('paymentAccountId', v)}
                    placeholder={t('reportPages.ai.review.accountToCredit')}
                  />
                </Labelled>
              ) : (
                <Labelled label={t('reportPages.ai.review.amountsAre')}>
                  <NativeSelect
                    aria-label={t('reportPages.ai.review.amountsAre')}
                    value={form.amountsAre}
                    onChange={(e) => set('amountsAre', e.target.value as ReviewForm['amountsAre'])}
                  >
                    <option value="exclusive">{t('reportPages.ai.review.exclusive')}</option>
                    <option value="inclusive">{t('reportPages.ai.review.inclusive')}</option>
                  </NativeSelect>
                </Labelled>
              )}
            </div>

            <div className="overflow-x-auto rounded-md border">
              <Table aria-label={t('reportPages.ai.review.lines')}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead className="min-w-[220px]">
                      {t('reportPages.ai.review.colAccount')}
                    </TableHead>
                    <TableHead className="min-w-[160px]">
                      {t('reportPages.ai.review.colDescription')}
                    </TableHead>
                    <TableHead className="w-[140px] text-end">
                      {t('reportPages.ai.review.colAmount')}
                    </TableHead>
                    {form.recordAs !== 'journal_entry' ? (
                      <TableHead className="w-[170px]">
                        {t('reportPages.ai.review.colTaxCode')}
                      </TableHead>
                    ) : null}
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.lines.map((l, i) => {
                    const pl = p?.lines[i];
                    return (
                      <TableRow key={i} data-testid="review-line">
                        <TableCell className="tnum align-top text-muted-foreground">
                          {i + 1}
                        </TableCell>
                        <TableCell className="align-top">
                          <Combobox
                            ariaLabel={t('reportPages.ai.lowField.lineAccount', { n: i + 1 })}
                            items={lineAccounts}
                            value={l.accountId}
                            onChange={(v) => setLine(i, { accountId: v })}
                            placeholder={t('reportPages.ai.review.chooseAccount')}
                            invalid={!l.accountId}
                          />
                          <div className="mt-1">
                            {chip(`lines.${i}.account`, pl?.account, l.accountId, (v) =>
                              setLine(i, { accountId: v as string }),
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <Input
                            aria-label={t('reportPages.ai.review.lineDescription', { n: i + 1 })}
                            value={l.description}
                            onChange={(e) => setLine(i, { description: e.target.value })}
                          />
                        </TableCell>
                        <TableCell className="align-top">
                          <AmountInput
                            aria-label={t('reportPages.ai.review.lineAmount', { n: i + 1 })}
                            className="text-end"
                            value={l.amount}
                            onValueChange={(v) => setLine(i, { amount: v })}
                            invalid={!l.amount}
                          />
                        </TableCell>
                        {form.recordAs !== 'journal_entry' ? (
                          <TableCell className="align-top">
                            <NativeSelect
                              aria-label={t('reportPages.ai.lowField.lineTaxCode', { n: i + 1 })}
                              value={l.taxCodeId ?? ''}
                              onChange={(e) => setLine(i, { taxCodeId: e.target.value || null })}
                            >
                              <option value="">{t('reportPages.ai.review.noTax')}</option>
                              {lineCodes.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.code} {nameFor(lang, t.name, t.nameAr)}
                                </option>
                              ))}
                            </NativeSelect>
                            <div className="mt-1">
                              {chip(`lines.${i}.taxCode`, pl?.taxCode, l.taxCodeId, (v) =>
                                setLine(i, { taxCodeId: v as string }),
                              )}
                            </div>
                          </TableCell>
                        ) : null}
                        <TableCell className="align-top">
                          {form.lines.length > 1 ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t('reportPages.ai.review.deleteLine', { n: i + 1 })}
                              onClick={() =>
                                setForm((f) => ({ ...f, lines: f.lines.filter((_, n) => n !== i) }))
                              }
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {editable ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    lines: [
                      ...f.lines,
                      { description: '', amount: '', accountId: null, taxCodeId: null },
                    ],
                  }))
                }
              >
                <Plus className="size-4" /> {t('reportPages.ai.review.addLine')}
              </Button>
            ) : null}
            {india && form.recordAs !== 'journal_entry' ? (
              <p className="text-xs text-muted-foreground">
                {t(
                  sale
                    ? p?.gstRegime === 'inter'
                      ? 'reportPages.ai.review.gstPlaceInter'
                      : p?.gstRegime
                        ? 'reportPages.ai.review.gstPlaceIntra'
                        : 'reportPages.ai.review.gstPlace'
                    : p?.gstRegime === 'inter'
                      ? 'reportPages.ai.review.gstVendorInter'
                      : p?.gstRegime
                        ? 'reportPages.ai.review.gstVendorIntra'
                        : 'reportPages.ai.review.gstVendor',
                )}
              </p>
            ) : null}

            <div className="ms-auto w-full max-w-xs space-y-1 text-sm" data-testid="totals">
              <Row
                label={t('reportPages.ai.review.subtotal')}
                value={preview.data ? `${ccy} ${formatAmount(preview.data.subtotal)}` : '—'}
              />
              <Row
                label={t('reportPages.ai.review.tax')}
                value={preview.data ? `${ccy} ${formatAmount(preview.data.tax)}` : '—'}
              />
              <Row
                label={t('reportPages.ai.review.total')}
                strong
                value={preview.data ? `${ccy} ${formatAmount(preview.data.total)}` : '—'}
              />
              {printedTotal && preview.data && preview.data.total !== printedTotal ? (
                <p className="text-xs text-warning">
                  {t('reportPages.ai.review.printedTotal', {
                    amount: ltr(`${ccy} ${formatAmount(printedTotal)}`),
                  })}
                </p>
              ) : null}
            </div>
          </fieldset>

          <VoucherPreview
            voucher={voucher}
            pending={preview.isFetching}
            error={preview.error ? errorText(preview.error) : null}
          />
          {d.usage ? (
            <p className="text-xs text-muted-foreground">
              {t('reportPages.ai.review.usage', {
                count: d.usage.requests,
                tokens: d.usage.inputTokens.toLocaleString('en-IN'),
              })}
            </p>
          ) : null}
        </section>
      </div>

      {editable || d.status === 'failed' ? (
        <footer className="fixed inset-x-0 bottom-0 z-20 border-t bg-card/95 px-6 py-3 backdrop-blur md:start-[var(--sidebar-width,16rem)]">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setRejecting(true)}>
              {t('reportPages.ai.review.reject')}
            </Button>
            <Button variant="outline" disabled={rerun.isPending} onClick={() => rerun.mutate()}>
              {t('reportPages.ai.list.rerun')}
            </Button>
            <div className="ms-auto flex flex-wrap items-center gap-2">
              {editable && missingInvoiceNo ? (
                <span className="text-sm text-destructive" data-testid="missing-invoice-no">
                  {t('reportPages.ai.review.missingInvoiceNo')}
                </span>
              ) : null}
              {editable && low.length ? (
                <span className="text-sm text-muted-foreground" data-testid="confirm-low">
                  {t('reportPages.ai.review.lowFields', { count: low.length })}
                </span>
              ) : null}
              {editable ? (
                <>
                  <Button variant="outline" disabled={save.isPending} onClick={() => save.mutate()}>
                    {t('reportPages.ai.review.saveForLater')}
                  </Button>
                  <Button
                    disabled={!canApprove || approve.isPending}
                    onClick={() => (low.length ? setConfirmingLow(true) : approve.mutate())}
                  >
                    {approve.isPending
                      ? t('reportPages.ai.review.creating')
                      : t('reportPages.ai.review.approve')}
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        </footer>
      ) : null}
      <Dialog open={confirmingLow} onOpenChange={(open) => !open && setConfirmingLow(false)}>
        <DialogContent data-testid="confirm-low-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('reportPages.ai.review.approveLowTitle', { count: low.length })}
            </DialogTitle>
            <DialogDescription>{t('reportPages.ai.review.approveLowBody')}</DialogDescription>
          </DialogHeader>
          <ul className="list-disc space-y-1 ps-5 text-sm">
            {low.map((key) => (
              <li key={key}>{lowFieldLabel(key, form.recordAs)}</li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingLow(false)}>
              {t('reportPages.ai.review.goBack')}
            </Button>
            <Button
              disabled={approve.isPending}
              onClick={() => {
                setConfirmed((prev) => new Set([...prev, ...low]));
                setConfirmingLow(false);
                approve.mutate();
              }}
            >
              {t('reportPages.ai.review.confirmApprove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReasonDialog
        open={rejecting}
        title={t('reportPages.ai.review.rejectTitle', { name: d.fileName })}
        description={t('reportPages.ai.review.rejectBody')}
        confirmLabel={t('reportPages.ai.review.rejectConfirm')}
        destructive
        pending={reject.isPending}
        error={reject.error ? errorText(reject.error) : null}
        onConfirm={(reason) => reject.mutate(reason)}
        onClose={() => setRejecting(false)}
      />
    </div>
  );
}

/**
 * The vendor of a bill or expense, or the customer of an invoice: pick one from the list, or
 * "Add vendor/customer <name>" with the name and GSTIN read from the document (created on approval).
 */
function PartyPicker({
  party,
  chip,
  items,
  value,
  fresh,
  india,
  read,
  onPick,
  onFresh,
}: {
  party: 'vendor' | 'customer';
  chip: ReactNode;
  items: ComboItem[];
  value: string | null;
  fresh: { name: string; gstin: string | null } | null;
  india: boolean;
  /** The name and GSTIN read from the document. */
  read: { name: string | null; gstin: string | null };
  onPick: (id: string | null) => void;
  onFresh: (n: { name: string; gstin: string | null } | null) => void;
}) {
  const t = useT();
  const v = party === 'vendor';
  const title = t(v ? 'reportPages.ai.lowField.vendor' : 'reportPages.ai.lowField.customer');
  return (
    <Labelled label={title} chip={chip}>
      {fresh && !value ? (
        <div
          className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-2 text-sm"
          data-testid={`new-${party}`}
        >
          <div className="font-medium">
            {t(v ? 'reportPages.ai.review.newVendor' : 'reportPages.ai.review.newCustomer', {
              name: fresh.name,
            })}
          </div>
          <Input
            aria-label={t(
              v ? 'reportPages.ai.review.newVendorName' : 'reportPages.ai.review.newCustomerName',
            )}
            value={fresh.name}
            onChange={(e) => onFresh({ ...fresh, name: e.target.value })}
          />
          {india ? (
            <Input
              aria-label={t(
                v
                  ? 'reportPages.ai.review.newVendorGstin'
                  : 'reportPages.ai.review.newCustomerGstin',
              )}
              placeholder={t('reportPages.ai.review.gstinOptional')}
              value={fresh.gstin ?? ''}
              onChange={(e) => onFresh({ ...fresh, gstin: e.target.value.toUpperCase() || null })}
            />
          ) : null}
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => onFresh(null)}
          >
            {t(
              v ? 'reportPages.ai.review.existingVendor' : 'reportPages.ai.review.existingCustomer',
            )}
          </button>
        </div>
      ) : (
        <Combobox
          ariaLabel={title}
          items={items}
          value={value}
          onChange={onPick}
          placeholder={t(
            v ? 'reportPages.ai.review.chooseVendor' : 'reportPages.ai.review.chooseCustomer',
          )}
          onAddNew={(typed) => onFresh({ name: typed || read.name || '', gstin: read.gstin })}
          addNewLabel={
            read.name
              ? t(
                  v
                    ? 'reportPages.ai.review.addVendorNamed'
                    : 'reportPages.ai.review.addCustomerNamed',
                  {
                    name: read.name,
                  },
                )
              : t(v ? 'reportPages.ai.review.addNewVendor' : 'reportPages.ai.review.addNewCustomer')
          }
        />
      )}
    </Labelled>
  );
}

function Labelled({
  label,
  chip,
  children,
}: {
  label: string;
  chip?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Field label={label}>
      {() => (
        <div className="space-y-1">
          {children}
          {chip ? <div>{chip}</div> : null}
        </div>
      )}
    </Field>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between gap-4', strong && 'border-t pt-1 font-semibold')}>
      <span>{label}</span>
      <span className="tnum text-end">{value}</span>
    </div>
  );
}

/** Read-only Dr/Cr voucher the approval will post (including input GST and A/P or bank). */
function VoucherPreview({
  voucher,
  pending,
  error,
}: {
  voucher: Voucher | null;
  pending: boolean;
  error: string | null;
}) {
  const t = useT();
  const names = useAccountNames();
  return (
    <div className="rounded-lg border bg-card p-4" data-testid="voucher">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{t('reportPages.ai.review.voucherTitle')}</h2>
        {pending ? (
          <Loader2
            className="size-4 animate-spin text-muted-foreground"
            aria-label={t('reportPages.ai.review.updating')}
          />
        ) : null}
      </div>
      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {!voucher ? (
        <p className="text-sm text-muted-foreground">{t('reportPages.ai.review.fillLines')}</p>
      ) : voucher.problem ? (
        <p className="text-sm text-destructive">
          {voucher.problem.charAt(0).toUpperCase() + voucher.problem.slice(1)}.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('reportPages.ai.review.colAccount')}</TableHead>
              <TableHead>{t('reportPages.ai.review.colDescription')}</TableHead>
              <TableHead className="text-end">{t('reportPages.ai.review.debits')}</TableHead>
              <TableHead className="text-end">{t('reportPages.ai.review.credits')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {voucher.lines.map((l, i) => (
              <TableRow key={i}>
                <TableCell>
                  {l.accountCode} {names.name(l.accountId, l.accountName)}
                </TableCell>
                <TableCell className="text-muted-foreground">{l.description}</TableCell>
                <TableCell className="tnum text-end">
                  {l.debitMinor !== '0' ? formatMinor(BigInt(l.debitMinor)) : ''}
                </TableCell>
                <TableCell className="tnum text-end">
                  {l.creditMinor !== '0' ? formatMinor(BigInt(l.creditMinor)) : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2}>
                {voucher.balanced ? (
                  <span className="inline-flex items-center gap-1 text-success">
                    <CheckCircle2 className="size-4" aria-hidden />{' '}
                    {t('reportPages.ai.review.balanced')}
                  </span>
                ) : (
                  <span className="text-destructive">
                    {t('reportPages.ai.review.outOfBalance')}
                  </span>
                )}
              </TableCell>
              <TableCell className="tnum text-end font-semibold">
                {voucher.currency} {formatMinor(BigInt(voucher.totalDebitMinor))}
              </TableCell>
              <TableCell className="tnum text-end font-semibold">
                {voucher.currency} {formatMinor(BigInt(voucher.totalCreditMinor))}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </div>
  );
}
